import React, {useEffect, useState} from 'react';
import { COUNT, DATA_CONFIGURATION, DECIMAL_CONVERSION, DEFAULT_VALUE, ENTER, FIELD_CLASS, INCREMENT, LABLE_FRACTION, LABLE_WHOLE_NUMBER, MANAGE, MAX, MAX_COUNT, MAX_QUANTITY, MIN, MIN_COUNT, MIN_QUANTITY, MIN_TOOL_TIP, QUANTITY, REQUIRED, UNITS } from '../ISRConstants';
import ToggleButton from 'Components/Common/components/Inputs/ToggleButton';
import { TConnector, TField, TFieldClass, TFieldDataConfiguration, TIndexState, TProductCategory, TSubmodel, TUpsertISRProjectDataModelsInput } from '../Pages/DefineModel/model';
import FractionalInput from './FractionalInput';
import {compareFractions, convertToDecimal, convertToFraction} from '../ISRHelper';
import OverlayIcon from 'Components/GMS/Rules/Components/OverlayIcon';
import {getAllProjectClass} from '../Pages/DefineModel/ModelServices/ModalServices';
import {ClassIcon, GearIcon} from 'assests/GmsIcons';
import ManageClassDialog from '../Pages/DefineModel/Components/ManageClassDialog';
import {useISRContext} from '../Hooks/useISRContext';
import ConfigurationFilterComponent from "./ConfigurationFilterComponent";

type TFractionConfigurationProps = {
    id: string;
    selectedItem: TField | TSubmodel | TProductCategory | TConnector | null;
    updateFieldDataConfiguration: (updatedConfig: TFieldDataConfiguration) => void;
    userProjectDataModels: TUpsertISRProjectDataModelsInput;
    indexInfo: TIndexState;
    projectId?: string;
}

const FractionConfiguration = ({id, selectedItem, updateFieldDataConfiguration, userProjectDataModels, indexInfo, projectId }: TFractionConfigurationProps) => {
    const [fieldDataConfiguration, setFieldDataConfiguration] = useState<TFieldDataConfiguration>({});
    const [showClassMenu, setShowClassMenu] = useState<boolean>(false);
    const [fieldName, setFieldName] = useState<string>('');
    const isrContext = useISRContext();
    const [errors, setErrors] = useState<{ [key: string]: string | null }>({ min: null, max: null, defaultValue: null, });
    const [showMinMaxError, setShowMinMax] = useState<boolean>(false);

    const openConnectorMenu = () => setShowClassMenu(true);

    useEffect(() => {
        if (selectedItem && 'belongsToDataModel' in selectedItem && selectedItem?.belongsToDataModel) {
            setFieldName(userProjectDataModels?.project.dataModels[indexInfo?.dataModelIndex ?? -1]?.data.fields[indexInfo?.fieldIndex ?? -1].name ?? "");
        } else {
            setFieldName(userProjectDataModels?.project.fields[indexInfo?.fieldOutsideDataModelIndex ?? -1]?.data?.name ?? "");
        }
        const existingValue = selectedItem && 'dataConfiguration' in selectedItem && selectedItem?.dataConfiguration ? selectedItem.dataConfiguration : {}
        setFieldDataConfiguration(existingValue);
    }, [selectedItem]);

    useEffect(() => {
        const {min, max} = fieldDataConfiguration || {};
        if (min && max) {
            setFieldDataConfiguration(prevConfig => ({
                ...prevConfig,
                decimalMin: convertToDecimal(min as string),
                decimalMax: convertToDecimal(max as string),
            }));
        }
    }, [fieldDataConfiguration?.min, fieldDataConfiguration?.max]);

    useEffect(() => {
        const hasChanges = Object.keys(fieldDataConfiguration).some(
            (key) => fieldDataConfiguration[key as keyof TFieldDataConfiguration] !== undefined
        );
        if (hasChanges) {
            updateFieldDataConfiguration(fieldDataConfiguration);
        }
    }, [fieldDataConfiguration]);

    useEffect(() => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalConversion: true}));
    }, []);

    // useEffect(() => {
    //     if (fieldDataConfiguration?.filter?.attributeId) {
    //         fetchAssociatedAttributes(fieldDataConfiguration?.filter?.categoryId);
    //     }
    // }, [fieldDataConfiguration?.filter?.categoryId]);


    const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, min: input}));
        if (fieldDataConfiguration.max && input) {
            const fractionValue = convertToFraction(input);

            if (compareFractions(fractionValue, convertToFraction(fieldDataConfiguration.max as string)) < 0) {
                setShowMinMax(false);
            } else {
                setShowMinMax(true);
            }
        }
    };

    const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, max: input}));
        if (fieldDataConfiguration.min && input) {
            const fractionValue = convertToFraction(input);
            if (compareFractions(fractionValue, convertToFraction(fieldDataConfiguration.min as string)) > 0) {
                setShowMinMax(false);
            } else {
                setShowMinMax(true);
            }
        }
    };

    const handleIncrementChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, increment: value}));
    };

    const handleUnitsChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, units: value}));
    };

    // const handleDecimalConversionChange = (value: boolean) => {
    //     const { min, max } = fieldDataConfiguration || {};
    //     if (value && min && max) {
    //         setFieldDataConfiguration(prevConfig => ({
    //             ...prevConfig,
    //             decimalMin: convertToDecimal(min as string),
    //             decimalMax: convertToDecimal(max as string),
    //         }));
    //     }
    //     setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalConversion: value})); 
    // };

    const handleDecimalMinChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalMin: value}));
    };

    const handleDecimalMaxChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, decimalMax: value}));
    };

    const handleLabelWholeNumberChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, labelWholeNumber: value}));
    };

    const handleLabelFractionChange = (value: string) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, labelFraction: value}));
    };

    const handleDefaultValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const input = e.target.value;
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, defaultValue: input}));
    };

    const handleRequiredChange = (value: boolean) => {
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, fieldRequired: value}));
    }

    const handleClassChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const selectedValue = event.target.value;
        const selectedObject = isrContext?.fieldClasses?.find((item: TFieldClass) => item.name === selectedValue);
        setFieldDataConfiguration((prevConfig) => ({...prevConfig, class: selectedObject}));
    };

    useEffect(() => {
        fetchAllClasses();
    }, []);

    const fetchAllClasses = async () => {
        try {
            const responseData = await getAllProjectClass(projectId ?? '');
            if (responseData?.done) {
                isrContext?.setFieldClasses(responseData?.data?.fieldClasses ?? []);
                isrContext?.setModelClasses(responseData?.data?.modelClasses ?? []);
            } else {
                //toast.error(responseData?.message);
            }
        } catch (error) {
            console.log('Error', error);
        }
    }

    const handleMinMaxFields = (value: number, property: string) => {
        setFieldDataConfiguration((prevConfig) => {
            if (!prevConfig) return prevConfig
            const updatedData = {
                ...prevConfig,
                [property]: value
            }
            return updatedData
        })
    }

    return (
        <React.Fragment>
            <div id={`${id}-data-config-component`} className='cart-style-dm'>
                <div id={`${id}-header`} className='tw-flex tw-justify-between tw-items-center'>
                    <label id={`${id}-title`} className='cart-title'>{DATA_CONFIGURATION}</label>
                    {showMinMaxError ? 
                        <label id={`${id}-min-max-error`} className='tw-text-red-500 tw-text-xs'>
                            {'Max should be greater than min value!'}
                        </label> 
                    : null}
                </div>
                <div id={`${id}-fraction-section`} className='tw-flex tw-items-center tw-mt-3'>
                    <div id={`${id}-min-fraction`} className='tw-w-[15%]'>
                        <div className='tw-flex tw-items-center tw-gap-1'>
                            <label id={`${id}-min-fraction-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
                            <OverlayIcon message={MIN_TOOL_TIP}/>
                        </div>
                        <FractionalInput 
                            id={`${id}-min-fraction-input`} 
                            fieldName='min' 
                            value={fieldDataConfiguration?.min ?? ''}
                            handleInputChange={handleMinChange} 
                            errors={errors} 
                            setErrors={setErrors}
                        />
                        {errors && errors?.min ?
                            <label id={`${id}-min-fraction-error`} className='tw-text-red-500 tw-text-xs'>{'Invalid fraction!'}</label> :
                            <label></label>}
                    </div>
                    <div id={`${id}-max-fraction`} className='tw-w-[15%] tw-ms-5'>
                        <div className='tw-flex tw-items-center tw-gap-1'>
                            <label id={`${id}-max-fraction-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
                            <OverlayIcon message={MIN_TOOL_TIP}/>
                        </div>
                        <FractionalInput 
                            id={`${id}-max-fraction-input`} 
                            fieldName='max' 
                            value={fieldDataConfiguration?.max ?? ''}
                            handleInputChange={handleMaxChange} 
                            errors={errors} 
                            setErrors={setErrors}
                        />
                        {errors && errors?.max ?
                            <label id={`${id}-max-fraction-error`} className='tw-text-red-500 tw-text-xs'>{'Invalid fraction!'}</label> :
                            <label></label>}
                    </div>
                    <div id={`${id}-decimal-conversion`} className='tw-w-[20%] tw-mb-auto tw-ms-5'>
                        <label id={`${id}-decimal-conversion-label`} className='tw-text-sm tw-text-[#747593] tw-block tw-mb-2'>{DECIMAL_CONVERSION}</label>
                        <ToggleButton
                            id={`${id}-decimal-conversion-toggle`}
                            name="dkdkd"
                            value={true}
                            onChange={(e) => {}}
                        />
                    </div>
                    <div id={`${id}-decimal-min`} className='tw-w-[15%]'>
                        <label id={`${id}-decimal-min-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
                        <input
                            id={`${id}-decimal-min-input`}
                            className="input-field-dm tw-bg-[#F4F5F7]"
                            placeholder={ENTER}
                            type='text'
                            step="any"
                            disabled={true}
                            onChange={(e) => handleDecimalMinChange(e.target.value)}
                            value={fieldDataConfiguration?.decimalMin ?? ''}
                        />
                    </div>
                    <div id={`${id}-decimal-max`} className='tw-w-[15%] tw-ms-5'>
                        <label id={`${id}-decimal-max-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
                        <input
                            id={`${id}-decimal-max-input`}
                            className="input-field-dm tw-bg-[#F4F5F7]"
                            placeholder={ENTER}
                            type='text'
                            step='any'
                            disabled={true}
                            onChange={(e) => handleDecimalMaxChange(e.target.value)}
                            value={fieldDataConfiguration?.decimalMax ?? ''}
                        />
                    </div>
                </div>

                <div id={`${id}-additional-inputs`} className='tw-flex tw-items-center tw-gap-5'>
                    <div id={`${id}-increment`} className='tw-w-[15%]'>
                        <label id={`${id}-increment-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{INCREMENT}</label>
                        <input
                            id={`${id}-increment-input`}
                            className="input-field-dm"
                            placeholder={ENTER}
                            type='text'
                            step='any'
                            onChange={(e) => handleIncrementChange(e.target.value)}
                            value={fieldDataConfiguration?.increment ?? ''}
                        />
                    </div>
                    <div id={`${id}-default-value`} className='tw-w-[15%]'>
                        <label id={`${id}-default-value-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{DEFAULT_VALUE}</label>
                        <FractionalInput 
                            id={`${id}-default-value-input`}
                            fieldName='defaultValue' 
                            value={fieldDataConfiguration?.defaultValue ?? ''}
                            handleInputChange={handleDefaultValueChange} 
                            errors={errors}
                            setErrors={setErrors}
                        />
                        {errors && errors?.defaultValue ?
                            <label id={`${id}-default-value-error`} className='tw-text-red-500 tw-text-xs'>{'Invalid fraction!'}</label> :
                            <label></label>}
                    </div>
                    <div id={`${id}-required`} className='tw-w-[10%] tw-mb-5'>
                        <label id={`${id}-required-label`} className='tw-text-sm tw-text-[#747593] tw-block tw-mb-1.5'>{REQUIRED}</label>
                        <ToggleButton
                            id={`${id}-required-toggle`}
                            name="Required"
                            value={fieldDataConfiguration?.fieldRequired ?? false}
                            onChange={(e) => handleRequiredChange(e.target.checked)}
                        />
                    </div>
                </div>

                <div id={`${id}-labels-section`} className='tw-flex tw-items-center tw-mt-3'>
                    <div id={`${id}-units`} className='tw-w-[20%]'>
                        <label id={`${id}-units-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{UNITS}</label>
                        <input
                            id={`${id}-units-input`}
                            className="input-field-dm"
                            placeholder={ENTER}
                            type='text'
                            onChange={(e) => handleUnitsChange(e.target.value)}
                            value={fieldDataConfiguration?.units ?? ''}
                        />
                    </div>
                    <div id={`${id}-whole-number`} className='tw-w-[20%] tw-ms-5'>
                        <label id={`${id}-whole-number-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{LABLE_WHOLE_NUMBER}</label>
                        <input
                            id={`${id}-whole-number-input`}
                            className="input-field-dm"
                            placeholder={ENTER}
                            type='text'
                            onChange={(e) => handleLabelWholeNumberChange(e.target.value)}
                            value={fieldDataConfiguration?.labelWholeNumber ?? ''}
                        />
                    </div>
                    <div id={`${id}-fraction-label`} className='tw-w-[20%] tw-ms-5'>
                        <label id={`${id}-fraction-label-text`} className='tw-text-sm tw-text-[#747593] tw-block'>{LABLE_FRACTION}</label>
                        <input
                            id={`${id}-fraction-label-input`}
                            className="input-field-dm"
                            placeholder={ENTER}
                            type='text'
                            onChange={(e) => handleLabelFractionChange(e.target.value)}
                            value={fieldDataConfiguration?.labelFraction ?? ''}
                        />
                    </div>
                </div>

                <div id={`${id}-decimal-info`} className="tw-w-auto tw-my-4 tw-h-9 tw-px-2.5 tw-py-2 tw-bg-gray-100 tw-rounded tw-border tw-border-zinc-200 tw-justify-start tw-items-center tw-gap-2.5 tw-inline-flex">
                    <div id={`${id}-decimal-info-text`} className="tw-text-sky-950 tw-text-sm tw-font-normal">
                        {`The associated decimal will be called "${fieldName} decimal"`}
                    </div>
                </div>

                <div id={`${id}-config-filter`}>
                    <ConfigurationFilterComponent 
                        id={`${id}-config-filter-component`}
                        fieldDataConfiguration={fieldDataConfiguration}
                        setFieldDataConfiguration={setFieldDataConfiguration}
                        userProjectDataModels={userProjectDataModels}
                        indexInfo={indexInfo} 
                        hasRange={true}
                    />
                </div>

                <div id={`${id}-field-class-header`} className='tw-flex tw-items-center tw-gap-1 tw-mt-4'>
                    <ClassIcon/>
                    <label id={`${id}-field-class-title`} className='cart-title'>{FIELD_CLASS}</label>
                </div>

                <div id={`${id}-field-class-content`} className='tw-flex tw-items-center tw-mt-1 tw-pb-3'>
                    <div id={`${id}-field-class-select`} className='tw-w-[30%] tw-pe-2'>
                        <select 
                            id={`${id}-field-class-dropdown`}
                            className={`drop-down-dm`} 
                            value={fieldDataConfiguration?.class?.name}
                            onChange={handleClassChange}
                        >
                            <option value={''}>Select Class</option>
                            {isrContext?.fieldClasses?.map((item: TFieldClass, index: number) => (
                                <option id={`${id}-class-option-${index}`} value={item?.name} key={item?.id ?? '' + index}>
                                    {item?.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <button 
                        id={`${id}-manage-connector-btn`}
                        onClick={openConnectorMenu}
                        className='tw-w-auto tw-cursor-pointer tw-flex tw-items-center tw-gap-1 tw-ms-4'
                    >
                        <GearIcon/>
                        <label className='tw-text-sm tw-text-primary tw-block'>{MANAGE}</label>
                    </button>
                </div>

                <>
                    <label id={`${id}-count-title`} className='cart-title tw-mt-2'>{COUNT}</label>
                    <div id={`${id}-count-inputs`} className='tw-flex tw-items-center tw-mt-2'>
                        <div id={`${id}-count-min`} className='tw-w-[20%]'>
                            <label id={`${id}-count-min-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
                            <input
                                id={`${id}-count-min-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.minCount ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MIN_COUNT)}
                                placeholder={ENTER}
                            />
                        </div>
                        <div id={`${id}-count-max`} className='tw-w-[20%] tw-ms-5'>
                            <label id={`${id}-count-max-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
                            <input
                                id={`${id}-count-max-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.maxCount ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MAX_COUNT)}
                                placeholder={ENTER}
                            />
                        </div>
                    </div>

                    <label id={`${id}-quantity-title`} className='cart-title tw-mt-2'>{QUANTITY}</label>
                    <div id={`${id}-quantity-inputs`} className='tw-flex tw-items-center tw-mt-2'>
                        <div id={`${id}-quantity-min`} className='tw-w-[20%]'>
                            <label id={`${id}-quantity-min-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MIN}</label>
                            <input
                                id={`${id}-quantity-min-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.minQuantity ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MIN_QUANTITY)}
                                placeholder={ENTER}
                            />
                        </div>
                        <div id={`${id}-quantity-max`} className='tw-w-[20%] tw-ms-5'>
                            <label id={`${id}-quantity-max-label`} className='tw-text-sm tw-text-[#747593] tw-block'>{MAX}</label>
                            <input
                                id={`${id}-quantity-max-input`}
                                className="input-field-dm"
                                type='number'
                                value={fieldDataConfiguration?.maxQuantity ?? 0}
                                onChange={(e) => handleMinMaxFields(Number(e.target.value), MAX_QUANTITY)}
                                placeholder={ENTER}
                            />
                        </div>
                    </div>
                </>

                {showClassMenu ? 
                    <ManageClassDialog 
                        id={`${id}-manage-class-dialog`}
                        showClassMenu={showClassMenu} 
                        setShowClassMenu={setShowClassMenu}
                        projectId={userProjectDataModels?.project?.id}
                    /> 
                : null}
            </div>
        </React.Fragment>
    );
}

export default FractionConfiguration;